// client/src/components/pages/Geofencing/useLexiStarStatusKeys.ts
//
// Live View's LexiStar statuses (ST-00253 / ST-00244): the status board's rows, read every 30 s,
// as registration → status key (StatusBoard/controlRoom statusKeyOfRow — the control room's
// placement). Live View colours a vehicle with this key, so it has the same colour as on the
// Status Board and Replay; a vehicle without a row (a tracker outside the LexiStar register, or
// a user who cannot see the board) falls back to its motion. A failed read keeps the last keys.
//
// ST-00276 phase 2 (map colours & states): useLexiStarDisplays also keeps every row's `display`
// (the organisation's display rules, evaluated by the server: base colour + overlay marks) and
// the board's `display_rules` (the rule set: its legend, and the rules a vehicle without a row is
// drawn by — map/assetDisplay displayOfMotion). useLexiStarStatusKeys stays the keys alone.

import { useEffect, useState } from 'react';
import { getStatusBoard as getStatusBoardJs } from '../../../services/lexistarStatusBoard';
import { statusKeyOfRow } from '../LexiStar/StatusBoard/controlRoom';
import { normaliseRegistration } from './vehicleStatus';
import type { AssetStatusKey } from '../../map/assetStatus';
import type { AssetDisplay } from '../../map/displayRules';
import type { StatusBoardResponse, StatusBoardRow } from '../../../types/lexistar-status-board';
import type { BoardDisplayRules } from '../../../types/lexistar-display-rules';

const getStatusBoard = getStatusBoardJs as unknown as (
  params?: Record<string, unknown>
) => Promise<StatusBoardResponse | null | undefined>;

export const STATUS_KEYS_POLL_MS = 30_000;

/** A row's registration (as the board's own rowRegistration: else the asset key's reg). */
const registrationOf = (row: StatusBoardRow): string =>
  row.registration || String(row.asset_key || '').replace(/^(reg|asset):/, '');

/** registration (normalised) → status key, for every status board row. */
export function statusKeysFromRows(
  rows: readonly StatusBoardRow[],
  now: number = Date.now()
): Map<string, AssetStatusKey> {
  const keys = new Map<string, AssetStatusKey>();
  rows.forEach((row) => {
    const reg = normaliseRegistration(registrationOf(row));
    if (reg) keys.set(reg, statusKeyOfRow(row, now));
  });
  return keys;
}

/** registration (normalised) → the server's display, for every row that carries one. */
export function displaysFromRows(rows: readonly StatusBoardRow[]): Map<string, AssetDisplay> {
  const out = new Map<string, AssetDisplay>();
  rows.forEach((row) => {
    const reg = normaliseRegistration(registrationOf(row));
    if (reg && row.display) out.set(reg, row.display);
  });
  return out;
}

/** What Live View draws its vehicles with. */
export interface LexiStarDisplays {
  keys: ReadonlyMap<string, AssetStatusKey>;
  displays: ReadonlyMap<string, AssetDisplay>;
  /** The organisation's rule set (null: no board for this user, or an older server). */
  rules: BoardDisplayRules | null;
}

const EMPTY: LexiStarDisplays = { keys: new Map(), displays: new Map(), rules: null };

/** The status board's keys, displays and rule set, every STATUS_KEYS_POLL_MS. */
export function useLexiStarDisplays(enabled = true): LexiStarDisplays {
  const [state, setState] = useState<LexiStarDisplays>(EMPTY);
  useEffect(() => {
    if (!enabled) return undefined;
    let live = true;
    const load = () =>
      Promise.resolve()
        .then(() => getStatusBoard({}))
        .then((body) => {
          if (!live || !Array.isArray(body?.rows)) return;
          const rows = body!.rows!;
          setState({ keys: statusKeysFromRows(rows), displays: displaysFromRows(rows), rules: body?.display_rules ?? null });
        })
        .catch(() => {
          /* no LexiStar status board for this user / tenant: motion colours */
        });
    void load();
    const id = setInterval(load, STATUS_KEYS_POLL_MS);
    return () => {
      live = false;
      clearInterval(id);
    };
  }, [enabled]);
  return state;
}

export default function useLexiStarStatusKeys(enabled = true): ReadonlyMap<string, AssetStatusKey> {
  return useLexiStarDisplays(enabled).keys;
}
